import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import groupService from '../api/groupService';
import surveyService from '../api/surveyService';
import waitlistService from '../api/waitlistService';
import { logPreviewShare, logStepCompletion } from '../api/onboardingService';
import SponsorMockupModal from '../components/modals/SponsorMockupModal';
import WaitlistProgressBanner from '../components/WaitlistProgressBanner';
import '../styles/CommunityOnboarding.css';

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { registerAdmin, currentUser, isAuthenticated } = useAuth();
  const { refreshAdminGroup, addNewAdminGroup, loading: groupLoading, adminGroups } = useGroup();
  const [communityName, setCommunityName] = useState('');
  const [nameError, setNameError] = useState('');
  const [checkingName, setCheckingName] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(null);
  const [activeTab, setActiveTab] = useState('events');
  const [currentStep, setCurrentStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [registrationError, setRegistrationError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [creationStep, setCreationStep] = useState(''); // Track creation progress
  const [isLoadingPreview, setIsLoadingPreview] = useState(false); // Track preview loading in step 1
  const [showHowItWorksTooltip, setShowHowItWorksTooltip] = useState(false); // Track tooltip visibility
  const [showAdminTooltip, setShowAdminTooltip] = useState(false); // Track admin button tooltip
  const [showShareTooltip, setShowShareTooltip] = useState(false); // Track share button tooltip
  const [isCommunityExpanded, setIsCommunityExpanded] = useState(false); // Track if community container is expanded
  const [hasEverExpanded, setHasEverExpanded] = useState(false); // Track if user has ever expanded
  const [isTabSwitching, setIsTabSwitching] = useState(false); // Track tab switching animation
  const [showSponsorModal, setShowSponsorModal] = useState(false); // Track sponsor modal visibility
  const [showExpandHint, setShowExpandHint] = useState(false); // Track expand hint visibility
  const [showShareModal, setShowShareModal] = useState(false); // Track share modal visibility
  const [shareEmail, setShareEmail] = useState(''); // Track share email input
  const [shareSuccess, setShareSuccess] = useState(false); // Track share success state
  const [uploadedLogo, setUploadedLogo] = useState(null); // Track uploaded logo
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null); // Track logo preview URL
  const [demoGroupId, setDemoGroupId] = useState('cde80c0e-75fd-4785-a0be-879afb0f5bc0'); // Demo group ID for preview iframe
  const [showSurveyModal, setShowSurveyModal] = useState(false); // Track survey modal visibility
  const [surveyStep, setSurveyStep] = useState(1); // Track current survey step
  const [isWaitlistMember, setIsWaitlistMember] = useState(false); // Track if email is on waitlist
  const [checkingWaitlist, setCheckingWaitlist] = useState(false); // Track waitlist check loading
  const [fromWaitlistGift, setFromWaitlistGift] = useState(false); // Track if user came from waitlist-gift route
  const [surveyData, setSurveyData] = useState({
    reasonsNotContinuing: [],
    otherReason: '',
    wantsHelp: false,
    helpType: '',
    helpEmail: '',
    additionalFeedback: '',
    triggerReason: '',
    timeOnStep3: 0
  }); // Track survey responses
  
  const [isFounderDeal, setIsFounderDeal] = useState(false); // Track if user came from Founder Deal
  const [awaitingEmailConfirmation, setAwaitingEmailConfirmation] = useState(false); // Track OTP verification
  const [otpCode, setOtpCode] = useState(''); // Track OTP input
  const [verifyingOtp, setVerifyingOtp] = useState(false); // Track OTP verification loading

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
  const displayName = communityName.trim() || "Your Community";
  const iframeRef = useRef(null);

  // Send updates to iframe via postMessage instead of remounting
  useEffect(() => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      iframeRef.current.contentWindow.postMessage({
        type: 'PREVIEW_UPDATE',
        name: displayName,
        logo: logoPreviewUrl
      }, window.location.origin);
    }
  }, [displayName, logoPreviewUrl]);

  // Check if user is already authenticated on component mount
  useEffect(() => {
    console.log('🔍 Component mounted - checking auth state');
    if (currentUser && isAuthenticated()) {
      // For admin users, wait for groups to load before redirecting
      if (currentUser.role === 'admin') {
        if (groupLoading) {
          console.log('🚀 Admin user authenticated on mount but groups still loading, waiting...');
          return;
        }
        
        if (!adminGroups || adminGroups.length === 0) {
          console.log('🚀 Admin user authenticated on mount but no admin groups available yet, waiting...');
          return;
        }
      }
      
      console.log('🚀 User is already authenticated on mount, redirecting to dashboard');
      console.log('🔍 User role:', currentUser.role);
      setIsRedirecting(true);
      
      // Redirect admin users to admin dashboard, members to regular dashboard
      const redirectPath = currentUser.role === 'admin' ? '/admin/dashboard/members' : '/dashboard';
      console.log('🔍 Redirecting to:', redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [currentUser, isAuthenticated, navigate, groupLoading, adminGroups]); // Added adminGroups dependency

  // Check for founder_deal parameter in URL
  useEffect(() => {
    // For HashRouter, we need to check window.location.hash which contains the query params
    const hashParts = window.location.hash.split('?');
    const queryString = hashParts.length > 1 ? hashParts[1] : location.search.substring(1);
    const urlParams = new URLSearchParams(queryString);
    const founderDealParam = urlParams.get('founder_deal');
    
    if (founderDealParam === 'true') {
      setIsFounderDeal(true);
      console.log('🔥 User is signing up via Founder Deal');
      console.log('🔍 Full hash:', window.location.hash);
    }
  }, [location.search, window.location.hash]);

  // Check if user came from waitlist-gift route
  useEffect(() => {
    // Check both location.state and URL parameters for waitlist gift indication
    const urlParams = new URLSearchParams(location.search);
    const fromWaitlistParam = urlParams.get('from') === 'waitlist-gift';
    const waitlistParam = urlParams.get('waitlist') === 'true';
    
    if (location.state?.fromWaitlistGift || fromWaitlistParam || waitlistParam) {
      setFromWaitlistGift(true);
    }
  }, [location.state, location.search]);

  const getTabColor = (tabName) => {
    const colors = {
      events: '#f97316',
      classes: '#10b981',
      prayer: '#8b5cf6'
    };
    return colors[tabName] || '#f97316';
  };

  const handleTabSwitch = (newTab) => {
    if (newTab === activeTab) return;
    
    setIsTabSwitching(true);
    setTimeout(() => {
      setActiveTab(newTab);
      setTimeout(() => {
        setIsTabSwitching(false);
      }, 150);
    }, 150);
  };

  const handleSponsorClick = () => {
    setShowSponsorModal(true);
  };

  const handleCloseSponsorModal = () => {
    setShowSponsorModal(false);
  };

  const handleExpandHintDismiss = () => {
    setShowExpandHint(false);
  };

  const handleShareClick = () => {
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setShareEmail('');
    setShareSuccess(false);
  };

  const handleShareSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Generate a session ID if none exists
      let sessionId = sessionStorage.getItem('sessionId');
      if (!sessionId) {
        sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('sessionId', sessionId);
      }

      // Log the share action to the database
      await logPreviewShare({
        sharedEmail: shareEmail,
        communityName: communityName.trim() || 'Unnamed Community',
        sessionId: sessionId
      });

      console.log('Preview share logged successfully for:', shareEmail);
    } catch (error) {
      console.error('Error logging preview share:', error);
      // Continue with success flow even if logging fails
    }
    
    // Show success message
    setShareSuccess(true);
    
    // Close modal after 2 seconds
    setTimeout(() => {
      handleCloseShareModal();
    }, 2000);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setUploadedLogo(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoRemove = () => {
    setUploadedLogo(null);
    setLogoPreviewUrl(null);
    // Clear the file input
    const fileInput = document.getElementById('logo-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSurveyTrigger = (triggerReason) => {
    console.log('🔍 Survey trigger called with reason:', triggerReason);
    console.log('🔍 Current step:', currentStep);
    setSurveyData(prev => ({
      ...prev,
      triggerReason,
      timeOnStep3: 0
    }));
    console.log('🔍 Setting showSurveyModal to true');
    setShowSurveyModal(true);
  };

  const handleSurveyClose = () => {
    const triggerReason = surveyData.triggerReason;
    const currentStepWhenTriggered = currentStep;
    
    setShowSurveyModal(false);
    setSurveyStep(1);
    setSurveyData({
      reasonsNotContinuing: [],
      otherReason: '',
      wantsHelp: false,
      helpType: '',
      helpEmail: '',
      additionalFeedback: '',
      triggerReason: '',
      timeOnStep3: 0
    });
    
    // If survey was triggered by back button, proceed with navigation
    if (triggerReason === 'back_button') {
      if (currentStepWhenTriggered === 1) {
        // Go back to main landing page from step 1
        navigate('/main-landing');
      } else if (currentStepWhenTriggered === 3) {
        // Go back to step 2 from step 3
        setCurrentStep(2);
      }
    }
  };

  const handleSurveySubmit = async () => {
    try {
      // Generate a session ID if none exists
      let sessionId = sessionStorage.getItem('sessionId');
      if (!sessionId) {
        sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('sessionId', sessionId);
      }

      // Prepare survey data for API
      const surveyPayload = {
        session_id: sessionId,
        user_id: currentUser?.id || null,
        community_name: communityName.trim() || null,
        reasons_not_continuing: surveyData.reasonsNotContinuing,
        other_reason: surveyData.otherReason || null,
        wants_help: surveyData.wantsHelp,
        help_type: surveyData.helpType || null,
        help_email: surveyData.helpEmail || null,
        additional_feedback: surveyData.additionalFeedback || null,
        trigger_reason: surveyData.triggerReason,
        time_on_step3: surveyData.timeOnStep3
      };

      console.log('Submitting survey data:', surveyPayload);
      
      // Submit to API
      const response = await surveyService.submitSurveyResponse(surveyPayload);
      console.log('Survey submitted successfully:', response);
      
      // Close survey
      handleSurveyClose();
    } catch (error) {
      console.error('Error submitting survey:', error);
      // Still close survey even if submission fails to not block user
      handleSurveyClose();
    }
  };

  const reasonOptions = [
    "I'm just exploring, not ready yet",
    "I'm not the right person to set this up",
    "I want to show it to someone else first",
    "I didn't understand what happens next",
    "It's not what I expected",
    "I had technical issues or confusion",
    "I don't see a use case for my community"
  ];

  const getEventsForTab = (tabName) => {
    const eventsData = {
      events: [
        {
          title: "Rosh Hashana Community Event",
          date: "📅 Sep 15, 2024 • 7:00 PM",
          location: "📍 Community Center",
          description: "Join us for a meaningful Rosh Hashana celebration with traditional foods, prayers, and community fellowship.",
          attendees: 24
        },
        {
          title: "Shabbat Lunch",
          date: "📅 Sep 20, 2024 • 6:30 PM",
          location: "📍 Main Hall",
          description: "Weekly Shabbat lunch with the community. Bring your family and friends for a wonderful afternoon.",
          attendees: 18
        },
        {
          title: "Sukkot Celebration",
          date: "📅 Oct 1, 2024 • 5:00 PM",
          location: "📍 Outdoor Pavilion",
          description: "Build and celebrate in our community Sukkah with festive meals and activities for all ages.",
          attendees: 32
        },
        {
          title: "Community Havdalah",
          date: "📅 Sep 21, 2024 • 8:15 PM",
          location: "📍 Garden Area",
          description: "Beautiful Havdalah ceremony under the stars with wine, spices, and community singing.",
          attendees: 15
        },
        {
          title: "Torah Study Group",
          date: "📅 Sep 25, 2024 • 7:30 PM",
          location: "📍 Study Room",
          description: "Weekly Torah study and discussion. All levels welcome to join our learning community.",
          attendees: 12
        },
        {
          title: "Yom Kippur Break Fast",
          date: "📅 Sep 28, 2024 • 8:00 PM",
          location: "📍 Main Hall",
          description: "Join us for a community break fast meal after Yom Kippur services.",
          attendees: 45
        }
      ],
      classes: [
        {
          title: "Talmud Study Circle",
          date: "📅 Every Thursday • 8:00 PM",
          location: "📍 Study Hall",
          description: "Deep dive into Talmudic texts with guided discussion and analysis.",
          attendees: 6
        },
        {
          title: "Hebrew Language Basics",
          date: "📅 Every Tuesday • 7:00 PM",
          location: "📍 Classroom A",
          description: "Learn Hebrew from scratch with our experienced teacher. Perfect for beginners.",
          attendees: 8
        },
        {
          title: "Jewish History Series",
          date: "📅 Every Sunday • 10:00 AM",
          location: "📍 Main Hall",
          description: "Explore 4,000 years of Jewish history from ancient times to modern day.",
          attendees: 14
        },
        {
          title: "Kabbalah & Mysticism",
          date: "📅 Every Wednesday • 7:30 PM",
          location: "📍 Meditation Room",
          description: "Introduction to Jewish mystical traditions and Kabbalistic teachings.",
          attendees: 10
        },
        {
          title: "Children's Hebrew School",
          date: "📅 Every Sunday • 9:00 AM",
          location: "📍 Youth Center",
          description: "Fun and engaging Hebrew lessons for children ages 6-12.",
          attendees: 22
        },
        {
          title: "Adult Bar/Bat Mitzvah Prep",
          date: "📅 Every Monday • 6:00 PM",
          location: "📍 Private Study",
          description: "Personalized preparation for adult B'nai Mitzvah celebration.",
          attendees: 4
        }
      ],
      prayer: [
        {
          title: "Morning Minyan",
          date: "📅 Daily • 7:00 AM",
          location: "📍 Sanctuary",
          description: "Daily morning prayers with the community. Coffee and light breakfast after.",
          attendees: 16
        },
        {
          title: "Evening Minyan",
          date: "📅 Daily • 6:30 PM",
          location: "📍 Sanctuary",
          description: "Evening prayer service. Join us for Maariv and community connection.",
          attendees: 12
        },
        {
          title: "Shabbat Morning Service",
          date: "📅 Every Saturday • 9:00 AM",
          location: "📍 Main Sanctuary",
          description: "Traditional Shabbat morning service with Torah reading and Kiddush.",
          attendees: 35
        },
        {
          title: "Friday Night Services",
          date: "📅 Every Friday • 6:00 PM",
          location: "📍 Sanctuary",
          description: "Welcome Shabbat with beautiful melodies and community prayers.",
          attendees: 28
        },
        {
          title: "Rosh Chodesh Gathering",
          date: "📅 Monthly • 7:00 PM",
          location: "📍 Circle Room",
          description: "Special prayers and celebration for the new Jewish month.",
          attendees: 20
        },
        {
          title: "Selichot Service",
          date: "📅 Sep 10, 2024 • 11:30 PM",
          location: "📍 Sanctuary",
          description: "Midnight Selichot prayers preparing for the High Holy Days.",
          attendees: 25
        }
      ]
    };
    
    return eventsData[tabName] || eventsData.events;
  };

  useEffect(() => {
    console.log('🔍 Auth state changed:', {
      currentUser: currentUser?.email,
      role: currentUser?.role,
      isAuthenticated: isAuthenticated(),
      currentStep: currentStep,
      creationStep: creationStep,
      groupLoading: groupLoading,
      adminGroupsCount: adminGroups?.length || 0,
      tokenInStorage: localStorage.getItem('token') ? 'exists' : 'missing'
    });
    
    // If user is authenticated and NOT in the middle of creation process, redirect to dashboard
    if (currentUser && isAuthenticated() && !creationStep && !sessionStorage.getItem('communityCreationInProgress')) {
      console.log('🚀 User is authenticated and not in creation process, redirecting to dashboard');
      console.log('🔍 User role:', currentUser.role);
      
      // For admin users, ensure admin groups are loaded before redirecting
      if (currentUser.role === 'admin') {
        console.log('🔍 Admin user - checking if admin groups are loaded...');
        console.log('🔍 Admin groups count:', adminGroups?.length || 0);
        console.log('🔍 Group loading state:', groupLoading);
        
        // Only redirect if admin groups have been loaded (either found groups or confirmed empty)
        if (!groupLoading && adminGroups !== null) {
          console.log('🚀 Admin groups loaded, redirecting to admin dashboard');
          setIsRedirecting(true);
          const redirectPath = '/admin/dashboard/members';
          console.log('🔍 Redirecting to:', redirectPath);
          navigate(redirectPath, { replace: true });
        } else {
          console.log('⏳ Waiting for admin groups to load before redirecting...');
        }
      } else {
        // For non-admin users, redirect immediately
        console.log('🚀 Non-admin user, redirecting immediately');
        setIsRedirecting(true);
        const redirectPath = '/dashboard';
        console.log('🔍 Redirecting to:', redirectPath);
        navigate(redirectPath, { replace: true });
      }
    }
  }, [currentUser, isAuthenticated, navigate, creationStep, adminGroups, groupLoading]);

  // Show expand hint after 5 seconds if community not expanded and user has never expanded
  useEffect(() => {
    if (currentStep === 2) {
      const timer = setTimeout(() => {
        if (!isCommunityExpanded && !hasEverExpanded) {
          setShowExpandHint(true);
        }
      }, 5000); // 5 seconds delay
      
      return () => clearTimeout(timer);
    }
  }, [currentStep, isCommunityExpanded, hasEverExpanded]);

  // Hide expand hint when community is expanded or user has ever expanded
  useEffect(() => {
    if (isCommunityExpanded || hasEverExpanded) {
      setShowExpandHint(false);
    }
  }, [isCommunityExpanded, hasEverExpanded]);

  // Handle beforeunload event for survey trigger
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentStep === 3 && !isLoading) {
        // Note: Can't show modal in beforeunload, but we can track the intent
        console.log('User attempting to leave step 3');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentStep, isLoading]);

  // Handle URL parameter to auto-open "How does Yesod work?" modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('modal') === 'how-it-works') {
      setShowHowItWorksTooltip(true);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep === 1) {
      if (!communityName.trim()) {
        return;
      }
      
      // Log step 1 completion
      try {
        // Generate a session ID if none exists
        let sessionId = sessionStorage.getItem('sessionId');
        if (!sessionId) {
          sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
          sessionStorage.setItem('sessionId', sessionId);
        }

        await logStepCompletion({
          sessionId: sessionId,
          communityName: communityName.trim(),
          stepReached: 1,
          completed: false
        });

        console.log('Step 1 completion logged successfully');
      } catch (error) {
        console.error('Error logging step 1 completion:', error);
        // Continue with the flow even if logging fails
      }
      
      // Go directly to step 2
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Log step 2 completion
      try {
        const sessionId = sessionStorage.getItem('sessionId');
        if (sessionId) {
          await logStepCompletion({
            sessionId: sessionId,
            communityName: communityName.trim(),
            stepReached: 2,
            completed: false
          });
          console.log('Step 2 completion logged successfully');
        }
      } catch (error) {
        console.error('Error logging step 2 completion:', error);
        // Continue with the flow even if logging fails
      }
      
      setCurrentStep(3);
    } else if (currentStep === 3) {
      setIsLoading(true);
      setRegistrationError('');
      
      const isCreating = sessionStorage.getItem('communityCreationInProgress');
      if (isCreating) {
        console.log('🎯 Creation already in progress, ignoring duplicate submission');
        return;
      }
      
      sessionStorage.setItem('communityCreationInProgress', 'true');
      
      try {
        // Step 1: Create Admin Account
        setCreationStep('creating-account');
        console.log('🔄 Registering admin user...');
        const adminData = {
          name: communityName.trim(),
          email: email.trim(),
          password: password,
          role: 'admin'
        };
        
        const registrationResult = await registerAdmin(adminData);
        console.log('✅ Admin user registered successfully:', registrationResult.user.email);
        console.log('🔍 Admin user role:', registrationResult.user.role);
        
        // Note: We'll mark waitlist as completed when user closes the success modal
        // This ensures the modal shows first, then gets marked as completed
        
        // Step 2: Create Community
        setCreationStep('creating-community');
        console.log('🔄 Creating group...');
        
        const groupData = {
          name: communityName.trim(),
          adminName: communityName.trim(),
          isFounderDeal: isFounderDeal // Pass Founder Deal flag
        };
        
        const groupResult = await groupService.createGroup(groupData);
        console.log('✅ Group created successfully:', groupResult.data.id);
        if (isFounderDeal) {
          console.log('🔥 Group created with Founder Deal flag');
        }
        
        // Step 2.5: Upload logo if user provided one
        if (uploadedLogo) {
          setCreationStep('uploading-logo');
          console.log('🖼️ Uploading logo to created group...');
          
          try {
            // Convert logo file to base64
            const logoBase64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(uploadedLogo);
            });
            
            const updateData = {
              name: communityName.trim(),
              description: groupResult.data.description || '',
              logoFile: logoBase64
            };
            
            const logoResult = await groupService.updateGroup(groupResult.data.id, updateData);
            console.log('✅ Logo uploaded successfully');
            
            // Update the groupResult with the new logo URL
            groupResult.data.logo_url = logoResult.data.logo_url;
          } catch (logoError) {
            console.warn('⚠️ Logo upload failed, but group was created successfully:', logoError);
            // Don't fail the entire process if logo upload fails
          }
        }
        
        // Step 3: Immediately add the new group to context (no API refresh needed)
        setCreationStep('finalizing');
        console.log('🎯 Adding newly created group to context immediately...');
        console.log('🔍 Group result data:', groupResult.data);
        console.log('🔍 Current user:', currentUser);
        console.log('🔍 Registration result user:', registrationResult.user);
        
        // Add the newly created group immediately to context using the registration result
        const success = addNewAdminGroup(groupResult.data, registrationResult.user.id);
        if (success) {
          console.log('✅ New admin group added to context immediately');
        } else {
          console.warn('⚠️ Failed to add group to context, falling back to refresh');
          // Force a complete refresh to ensure data is loaded
          await refreshAdminGroup();
        }
        
        sessionStorage.removeItem('communityCreationInProgress');
        
        console.log('🚀 Registration complete, preparing redirect');
        console.log('🚀 Registration result user role:', registrationResult.user.role);
        
        // Step 4: Add a small delay to ensure context update completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Log step 3 completion (onboarding finished)
        try {
          const sessionId = sessionStorage.getItem('sessionId');
          if (sessionId) {
            await logStepCompletion({
              sessionId: sessionId,
              communityName: communityName.trim(),
              stepReached: 3,
              completed: true
            });
            console.log('Step 3 completion (onboarding finished) logged successfully');
          }
        } catch (error) {
          console.error('Error logging step 3 completion:', error);
          // Don't fail the process if logging fails
        }
        
        // Clear creation step to allow useEffect redirect to take over
        setCreationStep(''); // This will trigger the useEffect which will handle the redirect properly
        setIsLoading(false);
        
        // The useEffect will now handle the redirect and ensure admin groups are loaded first
        
      } catch (error) {
        console.error('❌ Registration/Group creation failed:', error);
        setIsLoading(false);
        setIsRedirecting(false);
        setCreationStep(''); // Clear creation step
        
        sessionStorage.removeItem('communityCreationInProgress');
        
        const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
        
        // Check if error is about email not confirmed
        if (errorMessage.includes('Email not confirmed') || errorMessage.includes('email_not_confirmed')) {
          setAwaitingEmailConfirmation(true);
          setRegistrationError('Please check your email and enter the 6-digit confirmation code we sent you.');
        } else if (error.response?.data?.message) {
          setRegistrationError(error.response.data.message);
        } else if (error.message) {
          setRegistrationError(error.message);
        } else {
          setRegistrationError('Something went wrong. Please try again.');
        }
      }
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setRegistrationError('Please enter a valid 6-digit code');
      return;
    }

    setVerifyingOtp(true);
    setRegistrationError('');

    try {
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(
        'https://kcbpnlgnsdtvpsnlsrnp.supabase.co',
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImtjYnBubGduc2R0dnBzbmxzcm5wIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzE0NDA2NzMsImV4cCI6MjA0NzAxNjY3M30.YlrQBZpLGH8OZp-4xxIRiC7gYNBFxKRNc1C5nNZm3zk'
      );

      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: email,
        token: otpCode,
        type: 'signup'
      });

      if (verifyError) {
        throw verifyError;
      }

      // Success - email verified, now complete the registration
      setAwaitingEmailConfirmation(false);
      setOtpCode('');
      
      // Continue with community creation
      await handleSubmit(e);
    } catch (err) {
      setRegistrationError(err.message || 'Invalid code. Please try again.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleMaybeLater = () => {
    setIsLoading(true);
    setLoadingButton('maybe-later');
    
    setTimeout(() => {
      setIsLoading(false);
      setLoadingButton(null);
      setCurrentStep(3);
    }, 800);
  };

  const handleBack = () => {
    console.log('🔍 handleBack called, currentStep:', currentStep);
    if (currentStep === 3) {
      // Trigger survey before going back
      console.log('🔍 Step 3 - triggering survey');
      handleSurveyTrigger('back_button');
      return; // Don't navigate until survey is completed
    } else if (currentStep === 2) {
      console.log('🔍 Step 2 - going to step 1');
      setCurrentStep(1);
    } else {
      // Step 1 - trigger survey modal instead of navigating away
      console.log('🔍 Step 1 - triggering survey');
      handleSurveyTrigger('back_button');
    }
  };

  const checkShowRecaptcha = () => {
    // TEMPORARILY DISABLED FOR TESTING
  };

  // Function to check if email is on waitlist
  const checkWaitlistStatus = async (emailToCheck) => {
    if (!emailToCheck || !emailToCheck.includes('@')) {
      setIsWaitlistMember(false);
      return;
    }

    setCheckingWaitlist(true);
    try {
      const result = await waitlistService.checkEmailOnWaitlist(emailToCheck);
      setIsWaitlistMember(result.onWaitlist);
    } catch (error) {
      console.error('Error checking waitlist:', error);
      setIsWaitlistMember(false);
    } finally {
      setCheckingWaitlist(false);
    }
  };

  const handleCommunityNameChange = (e) => {
    const nameValue = e.target.value;
    setCommunityName(nameValue);
    
    // Clear previous error if user is typing
    if (nameError) {
      setNameError('');
    }
    
    // Debounce the name availability check
    if (nameValue && nameValue.trim().length >= 2) {
      const timeoutId = setTimeout(async () => {
        setCheckingName(true);
        try {
          const result = await groupService.checkNameAvailability(nameValue.trim());
          if (!result.available) {
            setNameError(result.message || 'This community name is already taken');
          } else {
            setNameError('');
          }
        } catch (error) {
          console.error('Error checking name availability:', error);
          // Don't show error on network issues, just log it
        } finally {
          setCheckingName(false);
        }
      }, 500); // 500ms debounce
      
      // Clear previous timeout
      if (window.nameCheckTimeout) {
        clearTimeout(window.nameCheckTimeout);
      }
      window.nameCheckTimeout = timeoutId;
    } else {
      setCheckingName(false);
    }
  };

  const handleEmailChange = (e) => {
    const emailValue = e.target.value;
    setEmail(emailValue);
    
    if (emailTouched && emailError) {
      setEmailError('');
    }
    
    // Check waitlist status with a debounce
    if (emailValue && emailValue.includes('@')) {
      const timeoutId = setTimeout(() => {
        checkWaitlistStatus(emailValue);
      }, 500); // 500ms debounce
      
      // Store timeout to clear if needed
      if (window.waitlistCheckTimeout) {
        clearTimeout(window.waitlistCheckTimeout);
      }
      window.waitlistCheckTimeout = timeoutId;
    } else {
      setIsWaitlistMember(false);
    }
    
    setTimeout(checkShowRecaptcha, 100);
  };

  const handleEmailBlur = (e) => {
    setEmailTouched(true);
    const emailValue = e.target.value;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailValue && !emailRegex.test(emailValue)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setTimeout(checkShowRecaptcha, 100);
  };

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={`community-onboarding ${currentStep === 1 ? 'step-1' : ''} ${fromWaitlistGift ? 'with-progress-banner' : ''}`}>
      {/* Waitlist Progress Banner */}
      <WaitlistProgressBanner isVisible={fromWaitlistGift} step={currentStep} />
      
      {/* Back button for step 1 only */}
      {currentStep === 1 && (
        <button
          type="button"
          className="step-1-back-button"
          onClick={handleBack}
        >
          ← Back
        </button>
      )}

      {isRedirecting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          flexDirection: 'column'
        }}>
          <div className="loading-spinner">
            <div className="spinner"></div>
            Taking you to your dashboard...
          </div>
        </div>
      )}

      <div className="onboarding-left">
        <div className="examples-container">
          
          {/* Modal can appear on any step when triggered by URL parameter */}
          <AnimatePresence>
            {showHowItWorksTooltip && (
              <motion.div 
                className="how-it-works-modal-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px'
                }}
                onClick={() => setShowHowItWorksTooltip(false)}
              >
                <motion.div 
                  className="how-it-works-modal"
                  initial={{ opacity: 0, y: -20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    background: 'white',
                    border: '1px solid #e1e5e9',
                    borderRadius: '16px',
                    padding: '32px',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
                    maxWidth: '680px',
                    width: '100%',
                    textAlign: 'left',
                    position: 'relative'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setShowHowItWorksTooltip(false)}
                    style={{
                      position: 'absolute',
                      top: '16px',
                      right: '16px',
                      background: 'transparent',
                      border: 'none',
                      fontSize: '24px',
                      color: '#9ca3af',
                      cursor: 'pointer',
                      padding: '4px',
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                  
                  <h3 style={{ 
                    margin: '0 0 20px 0', 
                    fontSize: '24px', 
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    🛠️ How Yesod Works
                  </h3>
                  
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ 
                      margin: '0 0 12px 0', 
                      fontSize: '18px', 
                      lineHeight: '1.6',
                      color: '#1f2937',
                      fontWeight: '600'
                    }}>
                      You manage. They see. Instantly.
                    </p>
                    
                    <p style={{ 
                      margin: '0 0 16px 0', 
                      fontSize: '16px', 
                      lineHeight: '1.6',
                      color: '#4b5563'
                    }}>
                      Your community lives in its own private space inside the Yesod app — only visible to your approved members.
                    </p>

                    <h4 style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '18px', 
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>
                      What Members See
                    </h4>
                    
                    <p style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '16px', 
                      lineHeight: '1.6',
                      color: '#4b5563'
                    }}>
                      Even before you add anything, the Yesod app gives members an organized, always-updated view of Jewish life:
                    </p>

                    <ul style={{ 
                      margin: '0 0 12px 0', 
                      paddingLeft: '20px',
                      fontSize: '16px', 
                      lineHeight: '1.6',
                      color: '#4b5563'
                    }}>
                      <li>Candle lighting times, zmanim, and Hebrew calendar</li>
                      <li>Holidays, fast days, and weekly Torah portions</li>
                      <li>Today's Hebrew date and a week-ahead view</li>
                      <li>All automatically based on their location — or one they set manually</li>
                      <li>Feels as easy as checking the weather</li>
                    </ul>

                    <h4 style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '18px', 
                      fontWeight: '600',
                      color: '#1f2937'
                    }}>
                      What You Can Add
                    </h4>
                    
                    <p style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '16px', 
                      lineHeight: '1.6',
                      color: '#4b5563'
                    }}>
                      From your private admin dashboard, you can publish:
                    </p>

                    <ul style={{ 
                      margin: '0 0 12px 0', 
                      paddingLeft: '20px',
                      fontSize: '16px', 
                      lineHeight: '1.6',
                      color: '#4b5563'
                    }}>
                      <li>📅 Events & classes</li>
                      <li>🕰️ Prayer schedules</li>
                      <li>❤️ Dedications & sponsorships</li>
                      <li>💳 Donation links</li>
                    </ul>

                    <p style={{ 
                      margin: '0', 
                      fontSize: '16px', 
                      lineHeight: '1.6',
                      color: '#4b5563'
                    }}>
                      Everything you publish appears instantly for members — no emails to send, no websites to update.
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          
          <div className="phone-frame">
            <div className="phone-screen">
              {/* Embed actual public community page in iframe for 100% accurate preview */}
              {/* Pass preview parameters to show user's custom name and logo */}
              <iframe
                ref={iframeRef}
                src={`${window.location.origin}/#/community/${demoGroupId}?preview=true&tab=1`}
                className="community-preview-iframe"
                title="Community Preview"
                sandbox="allow-same-origin allow-scripts allow-popups"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  overflow: 'hidden'
                }}
              />
            </div>
            

            
            <div className="phone-details">
              <div className="phone-speaker"></div>
              <div className="phone-home-indicator"></div>
            </div>
          </div>
        </div>
      </div>

      <div className="onboarding-right">
        <div className="top-right-link">
          <span>Already have a community? </span>
          <button 
            className="sign-in-link"
            onClick={() => navigate('/login')}
          >
            Sign in
          </button>
        </div>

        <div className="form-container">
          <AnimatePresence mode="wait">
            {currentStep === 1 ? (
              <motion.div
                key="step1"
                className="form-step"
                initial={{ opacity: 0, x: 0 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  className="form-header"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <h1>Create Your Community</h1>
                </motion.div>

                <motion.form
                  className="community-form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <div className="instructional-text">
                    <p>Type your community name below to get started</p>
                  </div>
                  
                  <div className="input-group">
                    <input
                      type="text"
                      id="communityName"
                      value={communityName}
                      onChange={handleCommunityNameChange}
                      placeholder="e.g., Beth Shalom, Chabad of Westville, MJE"
                      required
                      maxLength={100}
                      disabled={isLoading}
                      style={{ 
                        paddingLeft: '16px',
                        borderColor: nameError ? '#ef4444' : undefined
                      }}
                    />
                    {checkingName && (
                      <div style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        marginTop: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <div className="spinner-small" style={{
                          width: '12px',
                          height: '12px',
                          border: '2px solid #e5e7eb',
                          borderTopColor: '#3b82f6',
                          borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite'
                        }}></div>
                        Checking availability...
                      </div>
                    )}
                    {nameError && !checkingName && (
                      <div style={{
                        fontSize: '14px',
                        color: '#ef4444',
                        marginTop: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        ⚠️ {nameError}
                      </div>
                    )}
                  </div>

                  <motion.button
                    type="submit"
                    className="create-button"
                    disabled={!communityName.trim() || checkingName || !!nameError}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Next: Add Logo
                  </motion.button>
                  
                  <motion.button
                    type="button"
                    className="secondary-button"
                    onClick={() => setCurrentStep(3)}
                    disabled={!communityName.trim() || checkingName || !!nameError}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      background: 'transparent',
                      border: '2px solid #e5e7eb',
                      color: (!communityName.trim() || checkingName || !!nameError) ? '#d1d5db' : '#6b7280',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: (!communityName.trim() || checkingName || !!nameError) ? 'not-allowed' : 'pointer',
                      marginTop: '12px',
                      width: '100%',
                      transition: 'all 0.2s ease',
                      opacity: (!communityName.trim() || checkingName || !!nameError) ? 0.5 : 1,
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (communityName.trim()) {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.color = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (communityName.trim()) {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.color = '#6b7280';
                      }
                    }}
                  >
                    Skip
                  </motion.button>
                </motion.form>
              </motion.div>
            ) : currentStep === 2 ? (
              <motion.div
                key="step2"
                className="form-step"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.3 }}
              >
                <button
                  type="button"
                  className="top-left-back-button"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  ← Back
                </button>
                
                <motion.div
                  className="form-header"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <h1>Make It Yours — Add a Logo</h1>
                  <p style={{ marginBottom: '8px' }}>Add a logo to personalize your community. Skip if you want to do it later.</p>
                </motion.div>

                <motion.form
                  className="community-form"
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <div className="logo-upload-section" style={{ marginBottom: '8px' }}>
                    
                    {!logoPreviewUrl ? (
                      <div style={{ position: 'relative', maxWidth: '140px' }}>
                        <input
                          type="file"
                          id="logo-upload"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          style={{ display: 'none' }}
                        />
                        <label
                          htmlFor="logo-upload"
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '12px 8px',
                            border: '2px dashed #d1d5db',
                            borderRadius: '6px',
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: '#f9fafb',
                            color: '#6b7280',
                            fontSize: '11px',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.borderColor = '#9ca3af';
                            e.target.style.backgroundColor = '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.backgroundColor = '#f9fafb';
                          }}
                        >
                          <div style={{ marginBottom: '3px', fontSize: '14px' }}>📁</div>
                          <div>Click to upload</div>
                          <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>
                            PNG, JPG (max 5MB)
                          </div>
                        </label>
                      </div>
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '6px',
                        backgroundColor: '#f9fafb',
                        maxWidth: '140px'
                      }}>
                        <img
                          src={logoPreviewUrl}
                          alt="Logo preview"
                          style={{
                            width: '40px',
                            height: '40px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            border: '1px solid #d1d5db'
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ 
                            fontSize: '12px', 
                            fontWeight: '500', 
                            color: '#374151',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {uploadedLogo?.name}
                          </div>
                          <div style={{ fontSize: '10px', color: '#6b7280' }}>
                            {uploadedLogo && (uploadedLogo.size / 1024 / 1024).toFixed(1)} MB
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleLogoRemove}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '10px',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="button-group">
                    <div style={{ marginBottom: '8px', position: 'relative' }}>
                      <motion.button
                        type="submit"
                        className="create-button"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Next: Set Up Account
                      </motion.button>
                    </div>
                    
                    <div style={{ position: 'relative' }}>
                      <motion.button
                        type="button"
                        className="secondary-button"
                        onClick={() => setCurrentStep(3)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        style={{
                          background: 'transparent',
                          border: '2px solid #e5e7eb',
                          color: '#6b7280',
                          padding: '12px 24px',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          marginTop: '0px',
                          width: '100%',
                          transition: 'all 0.2s ease',
                          textAlign: 'center',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = '#d1d5db';
                          e.target.style.color = '#374151';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.color = '#6b7280';
                        }}
                      >
                        Skip & Continue
                      </motion.button>
                    </div>
                  </div>
                </motion.form>
              </motion.div>
            ) : (
              <motion.div
                key="step3"
                className="form-step"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.3 }}
              >
                <button
                  type="button"
                  className="top-left-back-button"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  ← Back
                </button>
                
                <motion.div
                  className="form-header"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <h1>Almost Done — Set Up Your Account</h1>
                  <p>Finish sign up to see your community dashboard</p>
                </motion.div>

                {awaitingEmailConfirmation ? (
                  <motion.form
                    className="community-form"
                    onSubmit={handleVerifyOtp}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    <div className="input-group">
                      <label htmlFor="otpCode" style={{ 
                        display: 'block', 
                        marginBottom: '12px',
                        fontSize: '15px',
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        Enter the 6-digit code from your email
                      </label>
                      <input
                        type="text"
                        id="otpCode"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength={6}
                        required
                        autoFocus
                        disabled={verifyingOtp}
                        style={{ 
                          paddingLeft: '16px', 
                          paddingRight: '16px',
                          fontSize: '24px',
                          letterSpacing: '8px',
                          textAlign: 'center',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>

                    {registrationError && (
                      <div className="registration-error">
                        {registrationError}
                      </div>
                    )}

                    <motion.button
                      type="submit"
                      className={`create-button ${verifyingOtp ? 'loading' : ''}`}
                      disabled={verifyingOtp || otpCode.length !== 6}
                      whileHover={{ scale: !verifyingOtp ? 1.02 : 1 }}
                      whileTap={{ scale: !verifyingOtp ? 0.98 : 1 }}
                    >
                      {verifyingOtp ? (
                        <div className="loading-text">
                          Verifying...
                        </div>
                      ) : (
                        'Verify Email'
                      )}
                    </motion.button>

                    <motion.button
                      type="button"
                      className="secondary-button"
                      onClick={() => {
                        setAwaitingEmailConfirmation(false);
                        setOtpCode('');
                        setRegistrationError('');
                      }}
                      disabled={verifyingOtp}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        background: 'transparent',
                        border: '2px solid #e5e7eb',
                        color: '#6b7280',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginTop: '12px',
                        width: '100%',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Back to Sign Up
                    </motion.button>
                  </motion.form>
                ) : (
                  <motion.form
                    className="community-form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    <div className="input-group">
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailBlur}
                        placeholder="Enter your email address"
                        required
                        disabled={isLoading}
                        style={{ paddingLeft: '16px', paddingRight: '16px' }}
                      />
                      {emailError && emailTouched && <div className="email-error">{emailError}</div>}
                      {isWaitlistMember && !emailError && (
                        <div className="waitlist-success-message">
                          ✨ You are a waitlist member! Finish signing up to unlock your free 1 year of Yesod Premium
                        </div>
                      )}
                    </div>

                    <div className="input-group">
                      <div className="password-input-container">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          value={password}
                          onChange={handlePasswordChange}
                          placeholder="Create a password"
                          required
                          minLength={8}
                          disabled={isLoading}
                          style={{ paddingLeft: '16px', paddingRight: '48px' }}
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={togglePasswordVisibility}
                          disabled={isLoading}
                          title={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                    </div>

                    {registrationError && (
                      <div className="registration-error">
                        {registrationError}
                      </div>
                    )}

                    {showRecaptcha && (
                      <motion.div 
                        className="recaptcha-container"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ReCAPTCHA
                          sitekey={RECAPTCHA_SITE_KEY}
                          onChange={handleRecaptchaChange}
                          onExpired={() => setRecaptchaValue(null)}
                          onError={() => setRecaptchaValue(null)}
                          theme="light"
                          size="normal"
                        />
                      </motion.div>
                    )}

                    <motion.button
                      type="submit"
                      className={`create-button ${isLoading || isRedirecting ? 'loading' : ''}`}
                      disabled={!email.trim() || !password.trim() || emailError || isLoading || isRedirecting}
                      whileHover={{ scale: !isLoading && !isRedirecting ? 1.02 : 1 }}
                      whileTap={{ scale: !isLoading && !isRedirecting ? 0.98 : 1 }}
                    >
                      {isRedirecting ? (
                        <div className="loading-text">
                          Taking you to your dashboard...
                        </div>
                      ) : isLoading ? (
                        <div className="loading-text">
                          Loading your dashboard...
                        </div>
                      ) : (
                        'Sign Up'
                      )}
                    </motion.button>
                  </motion.form>
                )}

              </motion.div>
            )}
          </AnimatePresence>

          {/* Terms footer - only show on step 3 */}
          {currentStep === 3 && (
            <div className="form-footer" style={{
              position: 'absolute',
              bottom: '20px',
              left: '20px',
              fontSize: '5px',
              opacity: 0.6,
              maxWidth: '300px',
              zIndex: 1000
            }}>
              <p style={{ margin: 0, lineHeight: '1', whiteSpace: 'nowrap' }}>
                By continuing, you agree to the{' '}
                <button 
                  type="button" 
                  className="link-button"
                  onClick={() => navigate('/terms-of-service')}
                  style={{ 
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 'inherit'
                  }}
                >
                  terms of service
                </button>{' '}
                and{' '}
                <button 
                  type="button" 
                  className="link-button"
                  onClick={() => navigate('/privacy-policy')}
                  style={{ 
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 'inherit'
                  }}
                >
                  privacy policy
                </button>
                .
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 99999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={handleCloseShareModal}
          >
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '32px',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                maxWidth: '400px',
                width: '100%',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleCloseShareModal}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '20px',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
              
              <h3 style={{ 
                margin: '0 0 8px 0', 
                fontSize: '20px', 
                fontWeight: '600',
                color: '#1f2937'
              }}>
                {shareSuccess ? 'Preview Sent!' : 'Share This Preview'}
              </h3>
              
              <p style={{ 
                margin: '0 0 24px 0', 
                fontSize: '14px', 
                lineHeight: '1.5',
                color: '#6b7280'
              }}>
                {shareSuccess 
                  ? `We've sent the community preview link to ${shareEmail}. Check your inbox!`
                  : "We'll send a link to this community preview to the email address below."
                }
              </p>
              
              {!shareSuccess ? (
                <form onSubmit={handleShareSubmit}>
                  <div style={{ marginBottom: '20px' }}>
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      placeholder="Enter email address"
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '16px',
                        boxSizing: 'border-box',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={handleCloseShareModal}
                      style={{
                        flex: 1,
                        background: 'transparent',
                        border: '2px solid #e5e7eb',
                        color: '#6b7280',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#d1d5db';
                        e.target.style.color = '#374151';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.color = '#6b7280';
                      }}
                    >
                      Cancel
                    </button>
                    
                    <button
                      type="submit"
                      disabled={!shareEmail.trim()}
                      style={{
                        flex: 1,
                        background: shareEmail.trim() ? '#3b82f6' : '#e5e7eb',
                        color: shareEmail.trim() ? 'white' : '#9ca3af',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: shareEmail.trim() ? 'pointer' : 'not-allowed',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (shareEmail.trim()) {
                          e.target.style.backgroundColor = '#2563eb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (shareEmail.trim()) {
                          e.target.style.backgroundColor = '#3b82f6';
                        }
                      }}
                    >
                      Send Preview
                    </button>
                  </div>
                </form>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '20px' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#10b981',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '16px'
                  }}>
                    <span style={{ fontSize: '24px', color: 'white' }}>✓</span>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Survey Modal */}
      <AnimatePresence>
        {showSurveyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleSurveyClose();
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '32px',
                boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '80vh',
                overflowY: 'auto',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={handleSurveyClose}
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  padding: '4px',
                  lineHeight: 1
                }}
              >
                ×
              </button>

              {surveyStep === 1 && (
                <div>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '24px', 
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    Before you go...
                  </h3>
                  
                  <p style={{ 
                    margin: '0 0 24px 0', 
                    fontSize: '16px', 
                    lineHeight: '1.5',
                    color: '#6b7280'
                  }}>
                    What best describes why you're not continuing right now?
                  </p>
                  
                  <div style={{ marginBottom: '24px' }}>
                    {reasonOptions.map((reason, index) => (
                      <label key={index} style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: '12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: '1.4'
                      }}>
                        <input
                          type="checkbox"
                          checked={surveyData.reasonsNotContinuing.includes(reason)}
                          onChange={(e) => {
                            const newReasons = e.target.checked
                              ? [...surveyData.reasonsNotContinuing, reason]
                              : surveyData.reasonsNotContinuing.filter(r => r !== reason);
                            setSurveyData(prev => ({ ...prev, reasonsNotContinuing: newReasons }));
                          }}
                          style={{
                            marginRight: '12px',
                            marginTop: '2px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        <span style={{ color: '#374151' }}>{reason}</span>
                      </label>
                    ))}
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      lineHeight: '1.4'
                    }}>
                      <input
                        type="checkbox"
                        checked={surveyData.reasonsNotContinuing.includes('other')}
                        onChange={(e) => {
                          const newReasons = e.target.checked
                            ? [...surveyData.reasonsNotContinuing.filter(r => r !== 'other'), 'other']
                            : surveyData.reasonsNotContinuing.filter(r => r !== 'other');
                          setSurveyData(prev => ({ ...prev, reasonsNotContinuing: newReasons }));
                        }}
                        style={{
                          marginRight: '12px',
                          marginTop: '2px',
                          accentColor: '#3b82f6'
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <span style={{ color: '#374151' }}>Other:</span>
                        {surveyData.reasonsNotContinuing.includes('other') && (
                          <input
                            type="text"
                            value={surveyData.otherReason}
                            onChange={(e) => setSurveyData(prev => ({ ...prev, otherReason: e.target.value }))}
                            placeholder="Please specify..."
                            style={{
                              width: '100%',
                              marginTop: '8px',
                              padding: '8px 12px',
                              border: '2px solid #e5e7eb',
                              borderRadius: '6px',
                              fontSize: '14px',
                              outline: 'none'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                          />
                        )}
                      </div>
                    </label>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={handleSurveyClose}
                      style={{
                        background: 'transparent',
                        border: '2px solid #e5e7eb',
                        color: '#6b7280',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Skip
                    </button>
                    
                    <button
                      onClick={() => {
                        const shouldShowStep2 = surveyData.reasonsNotContinuing.some(reason => 
                          ['I\'m not the right person to set this up', 'I want to show it to someone else first', 'I didn\'t understand what happens next'].includes(reason)
                        );
                        if (shouldShowStep2) {
                          setSurveyStep(2);
                        } else {
                          setSurveyStep(3);
                        }
                      }}
                      disabled={surveyData.reasonsNotContinuing.length === 0}
                      style={{
                        background: surveyData.reasonsNotContinuing.length > 0 ? '#3b82f6' : '#e5e7eb',
                        color: surveyData.reasonsNotContinuing.length > 0 ? 'white' : '#9ca3af',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: surveyData.reasonsNotContinuing.length > 0 ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {surveyStep === 2 && (
                <div>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '24px', 
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    Want help or to pick this up later?
                  </h3>
                  
                  <p style={{ 
                    margin: '0 0 24px 0', 
                    fontSize: '16px', 
                    lineHeight: '1.5',
                    color: '#6b7280'
                  }}>
                    Would you like to send this preview to a colleague or schedule a quick 10-min walkthrough?
                  </p>
                  
                  <div style={{ marginBottom: '24px' }}>
                    {[
                      { value: 'send_preview', label: 'Yes, send preview to someone else' },
                      { value: 'schedule_walkthrough', label: 'Yes, schedule a walkthrough' },
                      { value: 'no_thanks', label: 'No thanks' }
                    ].map((option) => (
                      <label key={option.value} style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '12px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input
                          type="radio"
                          name="helpType"
                          value={option.value}
                          checked={surveyData.helpType === option.value}
                          onChange={(e) => setSurveyData(prev => ({ ...prev, helpType: e.target.value, wantsHelp: e.target.value !== 'no_thanks' }))}
                          style={{
                            marginRight: '12px',
                            accentColor: '#3b82f6'
                          }}
                        />
                        <span style={{ color: '#374151' }}>{option.label}</span>
                      </label>
                    ))}
                    
                    {surveyData.helpType === 'send_preview' && (
                      <input
                        type="email"
                        value={surveyData.helpEmail}
                        onChange={(e) => setSurveyData(prev => ({ ...prev, helpEmail: e.target.value }))}
                        placeholder="Enter colleague's email address"
                        style={{
                          width: '100%',
                          marginTop: '12px',
                          padding: '12px 16px',
                          border: '2px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '16px',
                          outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                      />
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setSurveyStep(1)}
                      style={{
                        background: 'transparent',
                        border: '2px solid #e5e7eb',
                        color: '#6b7280',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Back
                    </button>
                    
                    <button
                      onClick={() => {
                        // Handle special case for schedule walkthrough
                        if (surveyData.helpType === 'schedule_walkthrough') {
                          // Open Calendly in new tab
                          window.open('https://calendly.com/shalom-yesod/30min', '_blank');
                          // Close survey and optionally submit data
                          handleSurveySubmit();
                          return;
                        }
                        setSurveyStep(3);
                      }}
                      style={{
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      {surveyData.helpType === 'schedule_walkthrough' ? 'Schedule Now' : 'Next'}
                    </button>
                  </div>
                </div>
              )}

              {surveyStep === 3 && (
                <div>
                  <h3 style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '24px', 
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    Anything else you want to share?
                  </h3>
                  
                  <p style={{ 
                    margin: '0 0 16px 0', 
                    fontSize: '16px', 
                    lineHeight: '1.5',
                    color: '#6b7280'
                  }}>
                    Any other feedback or thoughts? (Optional)
                  </p>
                  
                  <textarea
                    value={surveyData.additionalFeedback}
                    onChange={(e) => setSurveyData(prev => ({ ...prev, additionalFeedback: e.target.value }))}
                    placeholder="Share your thoughts..."
                    rows={4}
                    style={{
                      width: '100%',
                      marginBottom: '24px',
                      padding: '12px 16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '16px',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                  
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setSurveyStep(surveyData.reasonsNotContinuing.some(reason => 
                        ['I\'m not the right person to set this up', 'I want to show it to someone else first', 'I didn\'t understand what happens next'].includes(reason)
                      ) ? 2 : 1)}
                      style={{
                        background: 'transparent',
                        border: '2px solid #e5e7eb',
                        color: '#6b7280',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Back
                    </button>
                    
                    <button
                      onClick={handleSurveySubmit}
                      style={{
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Submit Feedback
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Progress Bar */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: 'white',
        padding: '12px 16px',
        borderRadius: '20px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb',
        zIndex: 1000
      }}>
        {[1, 2, 3].map((step) => (
          <div
            key={step}
            style={{
              width: '24px',
              height: '6px',
              borderRadius: '3px',
              backgroundColor: step <= currentStep ? '#3b82f6' : '#e5e7eb',
              transition: 'background-color 0.3s ease'
            }}
          />
        ))}
        <span style={{
          marginLeft: '8px',
          fontSize: '11px',
          fontWeight: '500',
          color: '#6b7280'
        }}>
          {currentStep}/3
        </span>
      </div>
    </div>
  );
};

export default SignUp;
